import { useCallback, useEffect, useState, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { quoteLineType, quoteType, vendorQuoteType } from '../../types'
import { useUser } from '../../hooks'
import axios from 'axios'
import { DropResult } from '@hello-pangea/dnd'
import { flattenBundledStructure, renumberFlatLineItems } from '../../helper'
import { BundleLineItem } from './lineItemPricing'
import { QUOTE_LINE_ITEMS_CHANGED_EVENT } from '../../Catalogs/Impres/impresCartSubmit'
import { mergeCategoryFields } from './mergeCategoryFields'
import { cacheQuoteCrm } from '../quoteCrmCache'

const PENDING_CATEGORY_CHECK_MS = 30_000
const PENDING_CATEGORY_MAX_CHECKS = 10

export type DrawerState = {
  type: 'edit' | 'new' | 'menu' | 'template-menu' | 'version-menu' | 'quote-menu' | 'header-crm-details' | null
  lineItem?: quoteLineType
  lineNumber?: number
  bundle?: string | null
  anchorEl?: HTMLElement | null
  anchorPosition?: { top: number; left: number } | null
}

export type SnackbarState = 'success' | 'fail' | null

export const BUNDLE_NON_EDIT_PROPERTIES: (keyof quoteLineType)[] = [
  'UnitCost',
  'ExtCost',
  'UnitPrice',
  'ExtPrice',
  'Margin',
  'ContractCeilingPrice',
  'MSRP',
  'TotalFees',
  'CreditCardFee',
  'PartnerFees',
  'ContractFees',
  'ProductCategory',
]

export interface LinesInterface {
  // State
  quote: quoteType
  setQuote: (quote: quoteType) => void
  lineItems: BundleLineItem[]
  setLineItems: React.Dispatch<React.SetStateAction<BundleLineItem[]>>
  lineItemsLoading: boolean
  setLineItemsLoading: (loading: boolean) => void
  page: number
  setPage: (page: number) => void
  rowsPerPage: number
  setRowsPerPage: (rows: number) => void
  selectedLineItems: quoteLineType[]
  setSelectedLineItems: (items: quoteLineType[] | ((prev: quoteLineType[]) => quoteLineType[])) => void
  drawerState: DrawerState
  setDrawerState: (state: DrawerState) => void
  cloneSnackbar: SnackbarState
  setCloneSnackbar: (state: SnackbarState) => void
  deleteSnackbar: SnackbarState
  setDeleteSnackbar: (state: SnackbarState) => void
  bulkEditSnackbar: SnackbarState
  setBulkEditSnackbar: (state: SnackbarState) => void
  bulkDeleteSnackbar: SnackbarState
  setBulkDeleteSnackbar: (state: SnackbarState) => void
  addLineSnackbar: SnackbarState
  setAddLineSnackbar: (state: SnackbarState) => void
  canEdit: boolean
  setCanEdit: (canEdit: boolean) => void
  bundleSnackbar: SnackbarState
  setBundleSnackbar: (state: SnackbarState) => void
  unbundleSnackbar: SnackbarState
  setUnbundleSnackbar: (state: SnackbarState) => void
  collapsedBundles: Set<string>
  setCollapsedBundles: (bundles: Set<string> | ((prev: Set<string>) => Set<string>)) => void
  lineCount: number
  quoteId: string | undefined
  vendorQuotes: vendorQuoteType[]
  versions: { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]
  setVersions: (versions: { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]) => void
  differences: { changes: quoteLineType[]; additions: quoteLineType[]; deletions: quoteLineType[] } | null
  setDifferences: (
    differences: { changes: quoteLineType[]; additions: quoteLineType[]; deletions: quoteLineType[] } | null,
  ) => void
  // Computed values
  sortedLineItems: (quoteLineType & { children: quoteLineType[] })[]
  // Handlers
  handleReloadLineItems: () => Promise<void>
  handleMenuAction: (action: 'edit' | 'new' | 'clone' | 'unbundle') => void
  handleClone: (lineItem: quoteLineType | null) => void
  handleDelete: (lineItem: quoteLineType | null) => void
  handleDeleteVendorQuote: (vendorQuoteId: string) => Promise<void>
  handleUnbundle: (lineItem: quoteLineType | null) => void
  handleBulkEdit: (lineItems: BundleLineItem[], flattened?: boolean) => Promise<void>
  handleBulkDelete: (lineItemIds: string[]) => Promise<void>
  handleDrag: (result: import('@hello-pangea/dnd').DropResult) => void
  handleEditModeToggle: () => Promise<void>
  handleResetLines: () => void
  hasUnsavedLineChanges: boolean
  handleBundle: (
    linesToBundle: quoteLineType[],
    selectedBundle: BundleLineItem | null,
    bundleSelection: BundleLineItem | string | null,
    bundleDescription: string,
  ) => Promise<void>
  handleAddLine: (lineItem: Partial<quoteLineType>) => Promise<void>
}

export function Lines(): LinesInterface {
  const { quoteId } = useParams()
  const location = useLocation()
  const user = useUser()
  const [quote, setQuote] = useState<quoteType>({} as quoteType)
  const [lineItems, setLineItems] = useState<BundleLineItem[]>([])
  const [lineItemsLoading, setLineItemsLoading] = useState(true)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedLineItems, setSelectedLineItems] = useState<quoteLineType[]>([])
  const [drawerState, setDrawerState] = useState<DrawerState>({ type: null })
  const [cloneSnackbar, setCloneSnackbar] = useState<SnackbarState>(null)
  const [deleteSnackbar, setDeleteSnackbar] = useState<SnackbarState>(null)
  const [bulkEditSnackbar, setBulkEditSnackbar] = useState<SnackbarState>(null)
  const [bulkDeleteSnackbar, setBulkDeleteSnackbar] = useState<SnackbarState>(null)
  const [addLineSnackbar, setAddLineSnackbar] = useState<SnackbarState>(null)
  const [canEdit, setCanEdit] = useState(false)
  const [lineItemsBeforeEdit, setLineItemsBeforeEdit] = useState<BundleLineItem[] | null>(null)
  const [bundleSnackbar, setBundleSnackbar] = useState<SnackbarState>(null)
  const [unbundleSnackbar, setUnbundleSnackbar] = useState<SnackbarState>(null)
  const [collapsedBundles, setCollapsedBundles] = useState<Set<string>>(new Set())
  const [lineCount, setLineCount] = useState(0)
  const [vendorQuotes, setVendorQuotes] = useState<vendorQuoteType[]>([])
  const [versions, setVersions] = useState<
    { QuoteId: string; Version: number; CreatedDate: string; Name: string | null }[]
  >([])
  const [differences, setDifferences] = useState<{
    changes: quoteLineType[]
    additions: quoteLineType[]
    deletions: quoteLineType[]
  } | null>(null)
  const handleReloadLineItems = async () => {
    if (!user.email || !quoteId) {
      return
    }
    setLineItemsLoading(true)
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        cacheQuoteCrm(quoteId, res.data.crmAccount, res.data.crmContact)
        setQuote(res.data.quote)
        setLineItems(res.data.lineItems)
        setVersions(res.data.versions)
        setDifferences(res.data.differences)
        setVendorQuotes(res.data.vendorQuotes ?? [])
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        setLineItemsLoading(false)
      })
  }

  const shouldRefreshAfterImport = Boolean((location.state as { refreshLineItems?: boolean } | null)?.refreshLineItems)

  useEffect(() => {
    handleReloadLineItems()
  }, [user.email, quoteId, shouldRefreshAfterImport])

  useEffect(() => {
    const handleQuoteLineItemsChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ quoteId: string }>).detail
      if (detail.quoteId.toLowerCase() === quoteId?.toLowerCase()) {
        handleReloadLineItems()
      }
    }
    window.addEventListener(QUOTE_LINE_ITEMS_CHANGED_EVENT, handleQuoteLineItemsChanged)
    return () => window.removeEventListener(QUOTE_LINE_ITEMS_CHANGED_EVENT, handleQuoteLineItemsChanged)
  }, [quoteId, user.email])

  // Also merge into the edit snapshot so these don't count as user changes
  const refreshCategories = useCallback(() => {
    if (!quoteId) return
    axios
      .get(`/quote/${quoteId}`)
      .then(res => {
        const fromServer = res.data.lineItems as BundleLineItem[]
        setLineItems(prev => mergeCategoryFields(prev, fromServer))
        setLineItemsBeforeEdit(prev => (prev ? mergeCategoryFields(prev, fromServer) : prev))
      })
      .catch(err => console.error(err))
  }, [quoteId])

  // Re-check lines waiting for the AI every 30s, for up to 5 minutes
  const hasPendingCategory = useMemo(
    () =>
      lineItems.some(
        line =>
          line.ProductCategorySource === 'PENDING_AI' ||
          (line.children ?? []).some(child => child.ProductCategorySource === 'PENDING_AI'),
      ),
    [lineItems],
  )
  useEffect(() => {
    if (!hasPendingCategory) return
    let checks = 0
    const timer = window.setInterval(() => {
      checks++
      refreshCategories()
      if (checks >= PENDING_CATEGORY_MAX_CHECKS) window.clearInterval(timer)
    }, PENDING_CATEGORY_CHECK_MS)
    return () => window.clearInterval(timer)
  }, [hasPendingCategory, refreshCategories])

  useEffect(() => {
    setLineCount(
      (lineItems as (quoteLineType & { children: quoteLineType[] })[])
        .flatMap(bundle => bundle.children)
        .filter(item => item.LineNumber !== -1).length +
        (lineItems as (quoteLineType & { children: quoteLineType[] })[]).filter(item => item.LineNumber !== -1).length,
    )
  }, [lineItems])

  const sortedLineItems = useMemo(() => {
    // Flatten all actual line items (excluding bundle headers) with their bundle info
    type FlatItem = {
      item: quoteLineType
      bundleHeader?: BundleLineItem
      bundleId: string | null
    }
    const flatItems: FlatItem[] = lineItems.flatMap(bundle =>
      bundle.LineNumber === -1
        ? (bundle.children ?? []).map<FlatItem>(child => ({
            item: child,
            bundleHeader: bundle,
            bundleId: bundle.Bundle || null,
          }))
        : ([{ item: bundle, bundleId: null }] as FlatItem[]),
    )

    // Get the items for the current page (only counting actual line items)
    const pageItems = flatItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

    // Group bundle items, then build result preserving order
    const bundleGroups = new Map<string, { header: BundleLineItem; items: quoteLineType[] }>()
    const result: (quoteLineType & { children: quoteLineType[] })[] = []
    const seenBundles = new Set<string>()

    for (const { item, bundleHeader, bundleId } of pageItems) {
      if (bundleId && bundleHeader) {
        if (!bundleGroups.has(bundleId)) {
          bundleGroups.set(bundleId, { header: bundleHeader, items: [] })
        }
        bundleGroups.get(bundleId)?.items.push(item)
      }
    }

    // Build result in order, adding bundles when first encountered
    for (const { item, bundleHeader, bundleId } of pageItems) {
      if (bundleId && bundleHeader) {
        if (!seenBundles.has(bundleId)) {
          seenBundles.add(bundleId)
          result.push({ ...bundleHeader, children: bundleGroups.get(bundleId)?.items ?? [] })
        }
      } else {
        result.push({ ...item, children: [] })
      }
    }

    return result
  }, [lineItems, page, rowsPerPage])

  const handleMenuAction = (action: 'edit' | 'new' | 'clone' | 'delete' | 'unbundle') => {
    const { lineItem } = drawerState
    switch (action) {
      case 'edit':
        setDrawerState({ type: 'edit', lineItem })
        break
      case 'new':
        if (lineItem?.LineNumber === -1) {
          setDrawerState({
            type: 'new',
            lineNumber: Math.max(
              ...(lineItems.find(bundle => bundle.Bundle === lineItem.Bundle)?.children ?? []).map(
                item => item.LineNumber,
              ),
            ),
            bundle: lineItem.Bundle,
          })
        } else {
          setDrawerState({ type: 'new', lineNumber: lineItem?.LineNumber, bundle: lineItem?.Bundle })
        }
        break
      case 'clone':
        handleClone(lineItem ?? null)
        setDrawerState({ type: null })
        break
      case 'unbundle':
        handleUnbundle(lineItem ?? null)
        setDrawerState({ type: null })
        break
      case 'delete':
        handleDelete(lineItem ?? null)
        setDrawerState({ type: null })
        break
    }
  }

  const handleClone = (lineItem: quoteLineType | null) => {
    if (!lineItem) return

    if (lineItem.LineNumber === -1) {
      const bundleToClone = lineItems.find(item => item.LineItemId === lineItem.LineItemId)
      if (!bundleToClone) return

      const allLineNumbers = lineItems
        .flatMap(bundle =>
          bundle.LineNumber === -1 ? (bundle.children ?? []).map(child => child.LineNumber) : [bundle.LineNumber],
        )
        .filter(num => num !== -1)
      const maxLineNumber = allLineNumbers.length > 0 ? Math.max(...allLineNumbers) : 0

      const newBundleId = crypto.randomUUID()
      const clonedHeader: quoteLineType = {
        ...bundleToClone,
        LineItemId: crypto.randomUUID(),
        Bundle: newBundleId,
      }
      let nextLineNumber = maxLineNumber + 1
      const clonedChildren: quoteLineType[] = (bundleToClone.children ?? []).map(child => ({
        ...child,
        LineItemId: crypto.randomUUID(),
        Bundle: newBundleId,
        LineNumber: nextLineNumber++,
      }))
      const newBundle: quoteLineType & { children: quoteLineType[] } = {
        ...clonedHeader,
        children: clonedChildren,
      }

      const bundleIndex = lineItems.findIndex(item => item.LineItemId === lineItem.LineItemId)
      const insertIndex = bundleIndex >= 0 ? bundleIndex + 1 : lineItems.length

      const updatedLineItems = [
        ...lineItems.slice(0, insertIndex),
        newBundle,
        ...lineItems.slice(insertIndex),
      ] as (quoteLineType & { children: quoteLineType[] })[]

      handleBulkEdit(updatedLineItems).catch(err => {
        console.error(err)
      })
    } else {
      axios
        .put(`/quote/${quoteId}/lineItem/${lineItem.LineItemId}/clone`, {
          quoteId: quoteId,
          lineNumber: lineItem.LineNumber,
        })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          setCloneSnackbar('success')
          const lineItemToEdit = lineItem.Bundle
            ? res.data.lineItems
                .find((bundle: quoteLineType & { children: quoteLineType[] }) => bundle.Bundle === lineItem.Bundle)
                ?.children.find((item: quoteLineType) => item.LineNumber === lineItem.LineNumber + 1)
            : res.data.lineItems.find((item: quoteLineType) => item.LineNumber === lineItem.LineNumber + 1)
          setDrawerState({ type: 'edit', lineItem: lineItemToEdit })
        })
        .catch(err => {
          console.error(err)
          setCloneSnackbar('fail')
        })
    }
  }

  const handleDelete = (lineItem: quoteLineType | null) => {
    if (!lineItem) return
    axios
      .delete(`/quote/${quoteId}/lineItem/${lineItem.LineItemId}`, {
        data: { lineNumber: lineItem.LineNumber },
      })
      .then(res => {
        setLineItems(res.data.lineItems)
        setQuote(res.data.quote)
        setDeleteSnackbar('success')
      })
      .catch(err => {
        console.error(err)
        setDeleteSnackbar('fail')
      })
  }
  const handleDeleteVendorQuote = (vendorQuoteId: string) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(`/quote/${quoteId}/vendorQuote/${vendorQuoteId}`)
        .then(res => {
          setVendorQuotes(res.data.vendorQuotes ?? [])
          setLineItems(res.data.lineItems ?? [])
          setQuote(res.data.quote)
          resolve()
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }
  const addToBundleAndRenumber = (
    bundledItems: BundleLineItem[],
    itemIdsToAdd: Set<string>,
    targetBundleId: string,
  ): quoteLineType[] => {
    const flatAllItems = flattenBundledStructure(bundledItems)
    const itemsToAdd = flatAllItems.filter(item => itemIdsToAdd.has(item.LineItemId) && item.LineNumber !== -1)
    const remainingItems = flatAllItems.filter(item => !itemIdsToAdd.has(item.LineItemId) || item.LineNumber === -1)

    const lastChildIndex = remainingItems.reduce(
      (lastIndex, item, index) => (item.Bundle === targetBundleId && item.LineNumber !== -1 ? index : lastIndex),
      -1,
    )
    const headerIndex = remainingItems.findIndex(item => item.Bundle === targetBundleId && item.LineNumber === -1)
    const insertIndex = lastChildIndex === -1 ? headerIndex + 1 : lastChildIndex + 1

    remainingItems.splice(insertIndex, 0, ...itemsToAdd.map(item => ({ ...item, Bundle: targetBundleId })))

    return renumberFlatLineItems(remainingItems)
  }

  const handleBundle = async (
    linesToBundle: quoteLineType[],
    selectedBundle: BundleLineItem | null,
    bundleSelection: BundleLineItem | string | null,
    bundleDescription: string,
  ) => {
    const previousLineItems = [...lineItems]
    const itemsToBundleIds = new Set(linesToBundle.filter(item => item.LineNumber !== -1).map(item => item.LineItemId))
    const itemsToBundle = linesToBundle.filter(item => item.LineNumber !== -1)

    const request =
      selectedBundle?.Bundle && selectedBundle.Bundle
        ? axios.put(`/quote/${quoteId}/bulkLineItems`, {
            lineItems: addToBundleAndRenumber(lineItems, itemsToBundleIds, selectedBundle.Bundle),
          })
        : axios.post(`/quote/${quoteId}/bundleLineItems`, {
            name: typeof bundleSelection === 'string' ? bundleSelection : '',
            description: bundleDescription,
            lineItems: itemsToBundle,
          })

    request
      .then(res => {
        setLineItems(res.data.lineItems)
        setQuote(res.data.quote)
        setBundleSnackbar('success')
        setSelectedLineItems([])
      })
      .catch(err => {
        console.error(err)
        setLineItems(previousLineItems)
        setBundleSnackbar('fail')
      })
  }

  const handleUnbundle = (lineItem: quoteLineType | null) => {
    if (!lineItem || !lineItem.Bundle) return

    const flatLineItems: quoteLineType[] = flattenBundledStructure(lineItems)

    if (lineItem.LineNumber === -1 || flatLineItems.filter(item => item.Bundle === lineItem.Bundle).length === 2) {
      const updatedLineItems: quoteLineType[] = flatLineItems
        .filter(item => item.Bundle === lineItem.Bundle && item.LineNumber !== -1)
        .map(item => ({ ...item, Bundle: null }))
      updatedLineItems.push(...flatLineItems.filter(item => item.Bundle !== lineItem.Bundle))
      axios
        .put(`/quote/${quoteId}/bulkLineItems`, { lineItems: updatedLineItems })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          setUnbundleSnackbar('success')
        })
        .catch(err => {
          console.error(err)
          setUnbundleSnackbar('fail')
        })
    } else {
      // Unbundle single item from bundle
      const updatedLineItems: quoteLineType[] = flatLineItems
        .filter(item => item.LineItemId === lineItem.LineItemId)
        .map(item => ({ ...item, Bundle: null }))
      updatedLineItems.push(...flatLineItems.filter(item => item.LineItemId !== lineItem.LineItemId))
      axios
        .put(`/quote/${quoteId}/bulkLineItems`, { lineItems: updatedLineItems })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          setUnbundleSnackbar('success')
        })
        .catch(err => {
          console.error(err)
          setUnbundleSnackbar('fail')
        })
    }
  }

  const handleAddLine = async (lineItem: Partial<quoteLineType>) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .post(`/quote/${quoteId}/lineItem`, { lineItem })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          resolve()
        })
        .catch(err => {
          console.error(err)
          reject(err)
        })
    })
  }

  const lineItemValuesEqual = (a: unknown, b: unknown): boolean => {
    if (a === b) return true
    if (a == null && b == null) return true
    if (a == null || b == null) return false
    if (a instanceof Date || b instanceof Date) {
      return new Date(a as Date).getTime() === new Date(b as Date).getTime()
    }
    if (typeof a === 'number' && typeof b === 'number') return a === b
    if (typeof a === 'boolean' && typeof b === 'boolean') return a === b
    const numA = Number(a)
    const numB = Number(b)
    if (
      (typeof a === 'number' || (typeof a === 'string' && a !== '' && !isNaN(numA))) &&
      (typeof b === 'number' || (typeof b === 'string' && b !== '' && !isNaN(numB)))
    ) {
      return numA === numB
    }
    return String(a) === String(b)
  }

  const lineItemsChanged = (previous: BundleLineItem[], current: BundleLineItem[]): boolean => {
    const previousFlat = flattenBundledStructure(previous)
    const currentFlat = flattenBundledStructure(current)

    if (previousFlat.length !== currentFlat.length) return true

    const currentById = new Map(currentFlat.map(item => [item.LineItemId, item]))

    return previousFlat.some(prevItem => {
      const currItem = currentById.get(prevItem.LineItemId)
      if (!currItem) return true

      return (Object.keys(prevItem) as (keyof quoteLineType)[]).some(key => {
        if (key === 'QuoteId') return false
        return !lineItemValuesEqual(prevItem[key], currItem[key])
      })
    })
  }

  const handleBulkEdit = async (lineItems: BundleLineItem[], flattened: boolean = false) => {
    return new Promise<void>((resolve, reject) => {
      const flatLineItems: quoteLineType[] = flattened ? lineItems : flattenBundledStructure(lineItems)
      axios
        .put(`/quote/${quoteId}/bulkLineItems`, { lineItems: flatLineItems })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          setBulkEditSnackbar('success')
          resolve()
        })
        .catch(err => {
          console.error(err)
          setBulkEditSnackbar('fail')
          reject(err)
        })
    })
  }

  const handleBulkDelete = async (lineItemIds: string[]) => {
    return new Promise<void>((resolve, reject) => {
      axios
        .delete(`/quote/${quoteId}/lineItems`, { data: { lineItemIds } })
        .then(res => {
          setLineItems(res.data.lineItems)
          setQuote(res.data.quote)
          setBulkDeleteSnackbar('success')
          resolve()
        })
        .catch(err => {
          console.error(err)
          setBulkDeleteSnackbar('fail')
          reject(err)
        })
    })
  }

  const handleEditModeToggle = async () => {
    if (lineItems.some(lineItem => !lineItem.LineItemId)) {
      await handleReloadLineItems()
    }

    if (!canEdit) {
      setLineItemsBeforeEdit(structuredClone(lineItems))
      setCanEdit(true)
      return
    }
    if (!lineItemsChanged(lineItemsBeforeEdit ?? [], lineItems)) {
      setCanEdit(false)
      setLineItemsBeforeEdit(null)
      return
    }
    setLineItemsLoading(true)
    try {
      await handleBulkEdit(lineItems)
      setCanEdit(false)
      setLineItemsBeforeEdit(null)
      setBulkEditSnackbar('success')
    } catch (err) {
      console.error('Error in handleMoveModeToggle: ', err)
      setBulkEditSnackbar('fail')
    } finally {
      setLineItemsLoading(false)
    }
  }

  const handleResetLines = () => {
    if (!canEdit || !lineItemsBeforeEdit) return
    setLineItems(structuredClone(lineItemsBeforeEdit))
  }

  const hasUnsavedLineChanges =
    canEdit && lineItemsBeforeEdit !== null && lineItemsChanged(lineItemsBeforeEdit, lineItems)

  const handleDrag = (result: DropResult) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return
    }

    type FlatItem = { item: quoteLineType; bundleId: string | null; isHeader: boolean }
    const flatItems: FlatItem[] = lineItems.flatMap(bundle => {
      const bundleId = bundle.Bundle || null
      const isHeader = bundle.LineNumber === -1
      return isHeader
        ? [
            { item: bundle, bundleId, isHeader: true },
            ...(bundle.children ?? []).map(lineItem => ({ item: lineItem, bundleId, isHeader: false })),
          ]
        : [{ item: bundle, bundleId: null, isHeader: false }]
    })

    const { index: sourceIndex } = result.source
    const { index: destIndex } = result.destination
    const sourceItem = flatItems[sourceIndex]

    const itemsAfterRemoval = flatItems.filter((_, i) => i !== sourceIndex)

    const wouldBlockOverlapBundle = (items: FlatItem[], insertIndex: number, blockLength: number): boolean => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].isHeader) {
          const bundleId = items[i].bundleId
          const bundleStart = i
          let bundleEnd = bundleStart
          while (++i < items.length && items[i].bundleId === bundleId && !items[i].isHeader) {
            bundleEnd = i
          }
          const interiorStart = bundleStart + 1
          if (insertIndex > bundleStart && insertIndex <= bundleEnd) return true
          if (blockLength > 1 && insertIndex < bundleStart && insertIndex + blockLength - 1 >= interiorStart) {
            return true
          }
          i--
        }
      }
      return false
    }

    const isInsertInSameBundle = (items: FlatItem[], bundleId: string, insertIndex: number): boolean => {
      for (let i = 0; i < items.length; i++) {
        if (items[i].isHeader && items[i].bundleId === bundleId) {
          const bundleStart = i
          let bundleEnd = bundleStart
          while (++i < items.length && items[i].bundleId === bundleId && !items[i].isHeader) {
            bundleEnd = i
          }
          return insertIndex > bundleStart && insertIndex <= bundleEnd
        }
      }
      return false
    }

    if (sourceItem.isHeader) {
      // Move entire bundle — dnd only removes the header, but we move the whole block
      const bundleId = sourceItem.bundleId
      const bundleStartIndex = flatItems.findIndex(f => f.bundleId === bundleId && f.isHeader)
      const bundleLength = flatItems.filter(f => f.bundleId === bundleId).length
      const itemsAfterBundleRemoval = flatItems.filter(
        (_, i) => i < bundleStartIndex || i >= bundleStartIndex + bundleLength,
      )
      const insertIndex = destIndex <= bundleStartIndex ? destIndex : destIndex - (bundleLength - 1)

      if (wouldBlockOverlapBundle(itemsAfterBundleRemoval, insertIndex, bundleLength)) return

      const itemsToMove = flatItems.splice(bundleStartIndex, bundleLength)
      flatItems.splice(insertIndex, 0, ...itemsToMove)
    } else if (sourceItem.bundleId) {
      // Move item within bundle - check boundaries using post-removal indices
      if (!isInsertInSameBundle(itemsAfterRemoval, sourceItem.bundleId, destIndex)) return

      const [movedItem] = flatItems.splice(sourceIndex, 1)
      flatItems.splice(destIndex, 0, movedItem)
    } else {
      // Move standalone item - reject if dropping into a bundle
      if (wouldBlockOverlapBundle(itemsAfterRemoval, destIndex, 1)) return

      const [movedItem] = flatItems.splice(sourceIndex, 1)
      flatItems.splice(destIndex, 0, movedItem)
    }

    let lineNumber = 1
    const updatedBundledItems: (quoteLineType & { children: quoteLineType[] })[] = []

    for (let i = 0; i < flatItems.length; i++) {
      const current = flatItems[i]

      if (current.isHeader) {
        const bundleId = current.bundleId
        const children: quoteLineType[] = []

        while (++i < flatItems.length && flatItems[i].bundleId === bundleId && !flatItems[i].isHeader) {
          children.push({ ...flatItems[i].item, LineNumber: lineNumber++, Bundle: bundleId })
        }
        i--

        updatedBundledItems.push({ ...current.item, LineNumber: -1, children })
      } else {
        updatedBundledItems.push({ ...current.item, LineNumber: lineNumber++, Bundle: null, children: [] })
      }
    }

    setLineItems(updatedBundledItems)
  }

  return {
    // State
    quote,
    setQuote,
    lineItems,
    setLineItems,
    lineItemsLoading,
    setLineItemsLoading,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    selectedLineItems,
    setSelectedLineItems,
    drawerState,
    setDrawerState,
    cloneSnackbar,
    setCloneSnackbar,
    deleteSnackbar,
    setDeleteSnackbar,
    bulkEditSnackbar,
    setBulkEditSnackbar,
    bulkDeleteSnackbar,
    setBulkDeleteSnackbar,
    addLineSnackbar,
    setAddLineSnackbar,
    canEdit,
    setCanEdit,
    bundleSnackbar,
    setBundleSnackbar,
    unbundleSnackbar,
    setUnbundleSnackbar,
    collapsedBundles,
    setCollapsedBundles,
    lineCount,
    quoteId,
    vendorQuotes,
    versions,
    setVersions,
    differences,
    setDifferences,
    // Computed values
    sortedLineItems,
    // Handlers
    handleReloadLineItems,
    handleMenuAction,
    handleClone,
    handleDelete,
    handleDeleteVendorQuote,
    handleUnbundle,
    handleBulkEdit,
    handleBulkDelete,
    handleDrag,
    handleEditModeToggle,
    handleResetLines,
    hasUnsavedLineChanges,
    handleBundle,
    handleAddLine,
  }
}
